import type { AppNode } from '@/nodes/types';
import { type InfoType, type InfoTypeId, isSelectField } from '@rock/types';
import { uniqBy } from 'lodash';

export type DataKeyInfo = {
    key: string;
    nodeId: string;
    nodeType: string;
};

export type DataKeySummaryEntry = {
    producers: DataKeyInfo[];
    consumers: DataKeyInfo[];
};

export function extractProducedDataKeys(nodes: AppNode[]): DataKeyInfo[] {
    const results: DataKeyInfo[] = [];
    for (const node of nodes) {
        const { data, id } = node;
        switch (data.type) {
            case 'yes-no':
            case 'select':
            case 'input':
            case 'address-lookup':
            case 'room-starter-selection':
                if (data.dataKey) results.push({ key: data.dataKey, nodeId: id, nodeType: data.type });
                break;
            case 'set':
                if (data.field) results.push({ key: data.field, nodeId: id, nodeType: data.type });
                break;
        }
    }
    return results;
}

export function extractConsumedDataKeys(nodes: AppNode[]): DataKeyInfo[] {
    const results: DataKeyInfo[] = [];
    for (const node of nodes) {
        const { data, id } = node;
        switch (data.type) {
            case 'if':
                if (data.field && !data.field.includes('{{')) {
                    results.push({ key: data.field, nodeId: id, nodeType: data.type });
                }
                break;
            case 'initial-rooms':
                if (data.floorsDataKey) {
                    results.push({ key: data.floorsDataKey, nodeId: id, nodeType: data.type });
                }
                if (data.hasBasementDataKey) {
                    results.push({ key: data.hasBasementDataKey, nodeId: id, nodeType: data.type });
                }
                // Raw flow data: nodes authored before preselect rules existed carry no array.
                for (const rule of data.preselectRooms ?? []) {
                    if (rule.dataKey) {
                        results.push({ key: rule.dataKey, nodeId: id, nodeType: data.type });
                    }
                }
                break;
        }
    }
    return results;
}

export type DataKeyValueOption = {
    value: string;
    title: string;
};

/**
 * The values a data key can hold, as stringified guide values, read off the nodes that produce it:
 * a select node's options, a select info type's options, or a yes-no's `true`/`false`. Empty when a
 * producer takes free text (or there is none).
 */
export function listDataKeyValueOptions(
    nodes: AppNode[],
    key: string,
    infoTypesById: Record<InfoTypeId, InfoType>,
): DataKeyValueOption[] {
    const producerIds = new Set(
        extractProducedDataKeys(nodes)
            .filter((p) => p.key === key)
            .map((p) => p.nodeId),
    );
    const options = nodes
        .filter((node) => producerIds.has(node.id))
        .flatMap(({ data }) => listProducerValueOptions(data, infoTypesById));
    return uniqBy(options, 'value');
}

export function findDisconnectedDataKeys(nodes: AppNode[]): DataKeyInfo[] {
    const producedKeys = new Set(extractProducedDataKeys(nodes).map((p) => p.key));
    return extractConsumedDataKeys(nodes).filter((c) => !producedKeys.has(c.key));
}

export function buildDataKeySummary(nodes: AppNode[]): Map<string, DataKeySummaryEntry> {
    const map = new Map<string, DataKeySummaryEntry>();

    const getOrCreate = (key: string): DataKeySummaryEntry => {
        let entry = map.get(key);
        if (!entry) {
            entry = { producers: [], consumers: [] };
            map.set(key, entry);
        }
        return entry;
    };

    for (const info of extractProducedDataKeys(nodes)) {
        getOrCreate(info.key).producers.push(info);
    }
    for (const info of extractConsumedDataKeys(nodes)) {
        getOrCreate(info.key).consumers.push(info);
    }

    return map;
}

function listProducerValueOptions(
    data: AppNode['data'],
    infoTypesById: Record<InfoTypeId, InfoType>,
): DataKeyValueOption[] {
    switch (data.type) {
        case 'select':
            return data.sections.flatMap((section) => section.options.map(({ value, title }) => ({ value, title })));
        case 'input': {
            const infoType = infoTypesById[data.infoTypeId];
            return infoType && isSelectField(infoType)
                ? (infoType.options ?? []).map(({ value, title }) => ({ value, title }))
                : [];
        }
        case 'yes-no':
            return [
                { value: 'true', title: data.yesLabel },
                { value: 'false', title: data.noLabel },
            ];
        default:
            return [];
    }
}
